/**
 * Topbar — sticky top bar inside <AppShell>.
 *
 * Default content:
 *  - search trigger (opens <SearchPalette>, also bound to ⌘K / Ctrl+K globally)
 *  - streak pill (amber flame + streak count from useStreak)
 *  - <NotificationBell />
 *  - Upgrade button (amber gradient, crown icon, hidden when isPremium)
 *
 * Consumers can override either slot to inject page-specific content
 * (e.g. an exam timer in ExamMode) by passing `searchSlot` and/or
 * `actionsSlot` props. Theme follows the app theme (light/dark).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStreak } from '../../hooks/useStreak';
import { useIsPremium } from '../../stores/premiumStore';
import { useTheme } from '../../hooks/useTheme';
import { useAuthStore } from '../../stores/authStore';
import NotificationBell from '../NotificationBell';
import { SearchPalette } from './SearchPalette';

export interface TopbarProps {
  /** Override the default search input slot. */
  searchSlot?: ReactNode;
  /** Override the default actions cluster (streak + bell + upgrade). */
  actionsSlot?: ReactNode;
}

/* ── Default search trigger — opens the ⌘K palette ──────────────────────── */

function DefaultSearch() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  /* Global ⌘K / Ctrl+K shortcut. */
  useEffect(() => {
    function handle(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, []);

  return (
    <div className="relative w-full max-w-[460px]">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative h-10 w-full cursor-text rounded-[10px] border border-slate-200 bg-slate-50 pl-9 pr-14 text-left text-sm text-slate-400 outline-none transition hover:border-slate-300 focus-visible:border-blue-500 focus-visible:ring-[3px] focus-visible:ring-blue-500/20 dark:border-white/10 dark:bg-white/5 dark:text-slate-500 dark:hover:border-white/20"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden="true">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </span>
        <span className="block truncate">{t('nav.searchPlaceholder')}</span>
        <kbd
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-400"
        >
          {t('nav.searchKbd')}
        </kbd>
      </button>
      <SearchPalette open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/* ── Default actions cluster ────────────────────────────────────────────── */

function DefaultActions() {
  const { t } = useTranslation();
  const streak = useStreak();
  const isPremium = useIsPremium();
  const { isDark, toggle } = useTheme();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return (
    <div className="flex items-center gap-2">
      {/* Streak pill (only shown for authenticated users) */}
      {isAuthenticated && (
        <div
          className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-amber-200/70 bg-amber-50 px-3 py-1.5 text-[12px] font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
          aria-label={t('nav.streakLabel', { count: streak, defaultValue: 'Current streak {{count}} days' })}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2c1 3-1 5-2 6-1.2 1.2-2 2.6-2 4.3A4 4 0 0 0 16 14c0-1-.3-2-1-3 1.7.5 3 2.3 3 4.3a6 6 0 0 1-12 0c0-3.3 2.5-5.6 4-7.6C11 6 12 4 12 2Z" />
          </svg>
          <span>{t('nav.streak')}</span>
          <span className="font-mono font-bold tabular-nums" style={{ fontFeatureSettings: '"tnum"' }}>{streak}</span>
        </div>
      )}

      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggle}
        className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5"
        aria-label={isDark ? t('header.switchToLight') : t('header.switchToDark')}
      >
        {isDark ? (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 7.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
          </svg>
        )}
      </button>

      {/* Notification bell (authed only) */}
      {isAuthenticated && <NotificationBell />}

      {/* Upgrade button — hidden for premium users */}
      {!isPremium && (
        <Link
          to="/pricing"
          className="inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-gradient-to-r from-amber-400 to-amber-500 px-3 text-[12.5px] font-bold text-[#1A1205] shadow-[0_6px_16px_-6px_rgba(245,158,11,0.5)] transition hover:from-amber-500 hover:to-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 18 5 7l5 5 2-9 2 9 5-5 2 11H3Z" />
          </svg>
          {t('nav.upgrade')}
        </Link>
      )}
    </div>
  );
}

/* ── Component ──────────────────────────────────────────────────────────── */

export function Topbar({ searchSlot, actionsSlot }: TopbarProps) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 dark:border-white/10 dark:bg-canvas-dark-2 sm:px-8">
      <div className="flex flex-1 min-w-0 items-center">
        {searchSlot ?? <DefaultSearch />}
      </div>
      <div className="flex flex-shrink-0 items-center">
        {actionsSlot ?? <DefaultActions />}
      </div>
    </header>
  );
}
